import { c as _c } from "react/compiler-runtime";
import { ErrorBoundary } from "@/components/error-boundary";
import { useIsTouchDevice } from "@/hooks/use-mobile";
import { mapDefaults, mapInteractionTimeouts } from "@/info/map-defaults";
import { useSearchPageInteractionStore } from "@/providers/search";
import { useOptimizedClicked } from "@/providers/search/hooks/use-optimized-clicked";
import { usePropertyInteractionStore } from "@/stores/property-interaction";
import type { MapItemWithCoodId } from "@/types/search";
import { logger } from "@/utils/logger";
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import React from "react";
import { cn } from "@/utils/cn";
import { SafePin } from "./safe-pin";
const MarkerFallback = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs relative"><div className="absolute top-full left-0 z-[-1]">{children}</div></div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

// Memoized individual marker component to prevent unnecessary re-renders
export const ProjectMarker = React.memo(function MarkerComponent(t0) {
  const $ = _c(69);
  const {
    marker,
    index,
    className,
    currentZoom
  } = t0;
  let t1;
  if ($[0] !== marker) {
    bb0: {
      if (!marker || !marker._id) {
        t1 = null;
        break bb0;
      }
      let coords;
      if (typeof marker._id === "string") {
        ;
        try {
          coords = JSON.parse(`[${marker._id}]`);
        } catch (t2) {
          const err = t2;
          logger.error("Failed to parse marker coordinates:", err);
          t1 = null;
          break bb0;
        }
      } else {
        if (Array.isArray(marker._id)) {
          coords = marker._id as [number, number];
        } else {
          logger.error("Invalid marker _id format:", marker._id);
          t1 = null;
          break bb0;
        }
      }
      if (!Array.isArray(coords) || coords.length !== 2 || isNaN(coords[0]) || isNaN(coords[1])) {
        logger.error("Invalid coordinates:" + JSON.stringify(coords), coords);
        t1 = null;
        break bb0;
      }
      t1 = coords;
    }
    $[0] = marker;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const coordinates = t1;
  let t2;
  if ($[2] !== coordinates) {
    t2 = coordinates?.toString() || "";
    $[2] = coordinates;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const coordinatesString = t2;
  const markerRef = React.useRef(null);
  const safePinRef = React.useRef(null);
  const projectNameRef = React.useRef(null);
  const isHoveredRef = React.useRef(false);
  const isGloballyHoveredRef = React.useRef(false);
  const isMobileTappedRef = React.useRef(false);
  const tapTimeoutRef = React.useRef(null);
  const {
    isClicked,
    handleToggleClick
  } = useOptimizedClicked(coordinatesString);
  const store = useSearchPageInteractionStore();
  const openPropertyOverlay = usePropertyInteractionStore(_temp);
  const isUnit = (marker.sups?.length || 0) > 0;
  const supsCount = marker.sups?.length || 0;
  const baseZIndex = supsCount + 1;
  const showNameOnHoverOnly = currentZoom < mapDefaults.projectNameZoomThreshold;
  const isTouchDevice = useIsTouchDevice();
  let t3;
  if ($[4] !== baseZIndex || $[5] !== isClicked || $[6] !== isTouchDevice || $[7] !== isUnit || $[8] !== showNameOnHoverOnly) {
    t3 = (isHovered, t4) => {
      const isTapped = t4 === undefined ? false : t4;
      if (!safePinRef.current) {
        return;
      }
      const element = safePinRef.current;
      element.classList.remove("scale-150", "!bg-secondary", "text-black", "shadow-lg", "!bg-accent", "text-accent-foreground");
      if (isHovered || isClicked) {
        element.classList.add("scale-150", "!bg-secondary", "text-black", "shadow-lg");
      }
      if (isUnit && !isHovered && !isClicked) {
        element.classList.add("!bg-accent", "text-accent-foreground");
      }
      if (markerRef.current) {
        const zIndex = isHovered || isGloballyHoveredRef.current || isClicked ? 9999 : baseZIndex;
        markerRef.current.zIndex = zIndex;
      }
      if (showNameOnHoverOnly && projectNameRef.current) {
        const shouldShow = isTouchDevice ? isTapped : isHovered;
        if (shouldShow) {
          projectNameRef.current.style.opacity = "1";
          projectNameRef.current.style.pointerEvents = "auto";
        } else {
          projectNameRef.current.style.opacity = "0";
          projectNameRef.current.style.pointerEvents = "none";
        }
      }
    };
    $[4] = baseZIndex;
    $[5] = isClicked;
    $[6] = isTouchDevice;
    $[7] = isUnit;
    $[8] = showNameOnHoverOnly;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const applyMarkerEffects = t3;
  let t4;
  let t5;
  if ($[10] !== applyMarkerEffects) {
    t4 = () => {
      applyMarkerEffects(isHoveredRef.current || isGloballyHoveredRef.current, isMobileTappedRef.current);
    };
    t5 = [applyMarkerEffects];
    $[10] = applyMarkerEffects;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  React.useEffect(t4, t5);
  let t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => () => {
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
    };
    t7 = [];
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  React.useEffect(t6, t7);
  let t8;
  if ($[15] !== applyMarkerEffects || $[16] !== coordinatesString || $[17] !== isTouchDevice || $[18] !== store) {
    t8 = () => {
      if (isTouchDevice) {
        return;
      }
      isHoveredRef.current = true;
      applyMarkerEffects(true, false);
      store.getState().setHoveredMarkerId(coordinatesString);
    };
    $[15] = applyMarkerEffects;
    $[16] = coordinatesString;
    $[17] = isTouchDevice;
    $[18] = store;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const handleMouseEnter = t8;
  let t9;
  if ($[20] !== applyMarkerEffects || $[21] !== isTouchDevice || $[22] !== store) {
    t9 = () => {
      if (isTouchDevice) {
        return;
      }
      isHoveredRef.current = false;
      applyMarkerEffects(false, false);
      store.getState().setHoveredMarkerId(null);
    };
    $[20] = applyMarkerEffects;
    $[21] = isTouchDevice;
    $[22] = store;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleMouseLeave = t9;
  let t10;
  if ($[24] !== applyMarkerEffects || $[25] !== isTouchDevice || $[26] !== showNameOnHoverOnly) {
    t10 = () => {
      if (!isTouchDevice || !showNameOnHoverOnly) {
        return;
      }
      isMobileTappedRef.current = !isMobileTappedRef.current;
      applyMarkerEffects(false, isMobileTappedRef.current);
      if (isMobileTappedRef.current) {
        if (tapTimeoutRef.current) {
          clearTimeout(tapTimeoutRef.current);
        }
        tapTimeoutRef.current = setTimeout(() => {
          isMobileTappedRef.current = false;
          applyMarkerEffects(false, false);
        }, mapInteractionTimeouts.MOBILE_TOOLTIP_AUTO_HIDE_MS);
      }
    };
    $[24] = applyMarkerEffects;
    $[25] = isTouchDevice;
    $[26] = showNameOnHoverOnly;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const handleTap = t10;
  let t11;
  let t12;
  if ($[28] !== applyMarkerEffects || $[29] !== coordinatesString || $[30] !== store) {
    t11 = () => {
      const unsubscribe = store.subscribe(_temp2, hoveredId => {
        const wasGloballyHovered = isGloballyHoveredRef.current;
        isGloballyHoveredRef.current = hoveredId === coordinatesString;
        if (wasGloballyHovered !== isGloballyHoveredRef.current) {
          applyMarkerEffects(isHoveredRef.current || isGloballyHoveredRef.current);
        }
      });
      return unsubscribe;
    };
    t12 = [coordinatesString, applyMarkerEffects, store];
    $[28] = applyMarkerEffects;
    $[29] = coordinatesString;
    $[30] = store;
    $[31] = t11;
    $[32] = t12;
  } else {
    t11 = $[31];
    t12 = $[32];
  }
  React.useEffect(t11, t12);
  let t13;
  if ($[33] !== coordinates || $[34] !== handleTap || $[35] !== handleToggleClick || $[36] !== marker || $[37] !== openPropertyOverlay) {
    t13 = () => {
      if (!marker || !coordinates) {
        return;
      }
      handleTap();
      if (marker.sups && marker.sups?.length > 0) {
        const supIds = marker.sups.map(_temp3);
        const initialSupId = supIds[0];
        openPropertyOverlay({
          sups: marker.sups,
          initialSupId,
          projectName: marker.projectName || ""
        });
      } else {
        if (marker.projectId) {
          openPropertyOverlay({
            sups: [],
            projectId: marker.projectId
          });
        }
      }
      handleToggleClick({
        coordinates,
        projectId: marker.projectId,
        projectName: marker.projectName,
        sups: marker.sups
      });
    };
    $[33] = coordinates;
    $[34] = handleTap;
    $[35] = handleToggleClick;
    $[36] = marker;
    $[37] = openPropertyOverlay;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const onClick = t13;
  if (!coordinates) {
    return null;
  }
  const markerClass = marker.isExactMatch ? "bg-primary text-white" : "bg-white text-gray-900";
  const t14 = `marker-${coordinatesString}-${index}`;
  let t15;
  if ($[39] !== coordinates[0] || $[40] !== coordinates[1]) {
    t15 = {
      lat: coordinates[1],
      lng: coordinates[0]
    };
    $[39] = coordinates[0];
    $[40] = coordinates[1];
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  const t16 = isClicked ? 9999 : baseZIndex;
  let t17;
  if ($[42] !== marker.isExactMatch || $[43] !== marker.projectName || $[44] !== markerClass || $[45] !== showNameOnHoverOnly) {
    t17 = marker.projectName && <div ref={projectNameRef} className={`absolute ${markerClass} -top-8 left-1/2 transform -translate-x-1/2 text-xs px-4 py-1 rounded-full whitespace-nowrap z-10 border border-gray-300 shadow-lg flex flex-col items-center justify-center transition-opacity duration-200`} style={{
      opacity: showNameOnHoverOnly ? 0 : 1,
      pointerEvents: showNameOnHoverOnly ? "none" : "auto"
    }}><span className="">{marker.projectName}</span><div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-t-[4px] border-transparent border-t-${marker.isExactMatch ? "primary" : "white"}`} /></div>;
    $[42] = marker.isExactMatch;
    $[43] = marker.projectName;
    $[44] = markerClass;
    $[45] = showNameOnHoverOnly;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const t18 = marker.sups?.length > 0 ? "!bg-secondary-700" : "!bg-secondary-200";
  let t19;
  if ($[47] !== t18) {
    t19 = cn("marker", t18, "!border-secondary-700");
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const t20 = marker.sups?.length > 0 ? "text-white" : "text-gray-400";
  let t21;
  if ($[49] !== t20) {
    t21 = cn("flex items-center justify-center", t20);
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  const t22 = marker.sups?.length ?? "0";
  let t23;
  if ($[51] !== t21 || $[52] !== t22) {
    t23 = <div className={t21}>{t22}</div>;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t19 || $[55] !== t23) {
    t24 = <SafePin ref={safePinRef} className={t19}>{t23}</SafePin>;
    $[54] = t19;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t17 || $[58] !== t24) {
    t25 = <div className="relative">{t17}{t24}</div>;
    $[57] = t17;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== className || $[61] !== handleMouseEnter || $[62] !== handleMouseLeave || $[63] !== onClick || $[64] !== t14 || $[65] !== t15 || $[66] !== t16 || $[67] !== t25) {
    t26 = <ErrorBoundary fallback={MarkerFallback}><AdvancedMarker key={t14} position={t15} onClick={onClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} zIndex={t16} ref={markerRef} className={className}>{t25}</AdvancedMarker></ErrorBoundary>;
    $[60] = className;
    $[61] = handleMouseEnter;
    $[62] = handleMouseLeave;
    $[63] = onClick;
    $[64] = t14;
    $[65] = t15;
    $[66] = t16;
    $[67] = t25;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  return t26;
}, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  const m1 = prevProps.marker;
  const m2 = nextProps.marker;
  return m1._id === m2._id && m1.sups?.length === m2.sups?.length && prevProps.index === nextProps.index && m1.projectId === m2.projectId && prevProps.currentZoom === nextProps.currentZoom;
});
function _temp(state) {
  return state.openPropertyOverlay;
}
function _temp2(state_0) {
  return state_0.hoveredMarkerId;
}
function _temp3(sup) {
  return sup._id;
}